/**
 * pin-history — pure history-list logic for the voice call's pinned-card
 * stack (design turn 14, "never a second inbox"). Extracted from
 * usePinnedCard.ts so the push/cap/bring-to-front/dismiss-front rules — and
 * the row/badge summary the mobile stack UI (14c) needs — are unit-testable
 * without React, mirroring the codebase's convention for hook-adjacent pure
 * logic (useHandoff.ts's phase transitions, connectionReducer,
 * send-routing.ts).
 */

import type { PinnedCard } from './pin-routing';
import { deriveMediaKind } from '@/components/assistant/thinking/media-kind';

/** One pin in the call's history, newest first. `id` is minted once at pin
 *  time and never reused, so `bringToFront` can target an exact entry even
 *  if two pins happen to hold visually-identical content. */
export interface PinEntry {
  id: string;
  pinnedAt: number;
  card: PinnedCard;
}

/** Owner decision ("never a second inbox"): the stack holds at most this
 *  many pins — the oldest silently drops off once a new pin exceeds it. */
export const MAX_PINS = 5;

/** A fresh pin always goes to the front, unconditionally — even a re-pin of
 *  content similar to what's already showing is still a new pin (no
 *  de-dup/merge heuristic; see usePinnedCard.ts). */
export function pushPin(history: PinEntry[], entry: PinEntry): PinEntry[] {
  return [entry, ...history].slice(0, MAX_PINS);
}

/** The dismiss (x) affordance removes ONLY the front pin — the next most
 *  recent becomes front, if any. Never touches the rest of the stack. */
export function dismissFrontPin(history: PinEntry[]): PinEntry[] {
  return history.slice(1);
}

/** Tapping an older row in the expanded stack (14c) promotes it to front,
 *  preserving the relative order of everything else. A no-op (same array
 *  reference back) if `id` isn't found or is already at the front. */
export function bringToFront(history: PinEntry[], id: string): PinEntry[] {
  const idx = history.findIndex((entry) => entry.id === id);
  if (idx <= 0) return history;
  const target = history[idx];
  const rest = history.slice(0, idx).concat(history.slice(idx + 1));
  return [target, ...rest];
}

export type PinRowKind = 'course' | 'video' | 'article';

export interface PinRowInfo {
  kind: PinRowKind;
  title: string;
}

/** Row/badge summary for one pin — courses take the first (soonest) course's
 *  title; a mixed-title pin (title-less multi-location search, FB33) is thus
 *  summarized by its lead card, which is the one visible when the pin is
 *  front. Media takes the first ref,
 *  binary-classified video/article via the SAME deriveMediaKind classifier
 *  pinned-card.tsx's MediaBody and the Thinking timeline use, so "is this a
 *  video or an article" never drifts between surfaces. */
export function derivePinRowInfo(card: PinnedCard): PinRowInfo {
  if (card.kind === 'courses') {
    return { kind: 'course', title: card.courses[0]?.title ?? '' };
  }
  const first = card.refs[0];
  const kind: PinRowKind =
    first && deriveMediaKind(first) === 'article' ? 'article' : 'video';
  return { kind, title: first?.title ?? '' };
}

const KIND_LABEL: Record<PinRowKind, string> = {
  course: 'Course',
  video: 'Video',
  article: 'Article',
};

/** Human label for a row's kind ("Course"/"Video"/"Article") — the same
 *  vocabulary the front-card badge (frontBadgeLabel below) and the expanded
 *  stack's row subline share. */
export function pinRowKindLabel(kind: PinRowKind): string {
  return KIND_LABEL[kind];
}

/** Front-card badge label (14c): a lone pin keeps "Pinned by Sara" — the
 *  original turn-11 chrome. Once history holds more than one pin, the badge
 *  names the KIND instead: the bare label collapsed, "<Kind> · latest"
 *  expanded (per the design mocks). */
export function frontBadgeLabel(
  card: PinnedCard,
  historyLength: number,
  expanded: boolean
): string {
  if (historyLength <= 1) return 'Pinned by Sara';
  const label = pinRowKindLabel(derivePinRowInfo(card).kind);
  return expanded ? `${label} · latest` : label;
}
